/**
 * Polls API Contracts (feed polls, SCH-2238)
 *
 * Endpoints under `/v1/polls` (KTD7). A poll is its own entity with hosts
 * beyond posts later, so its routes are not under `/feeds`. Every write
 * returns the fresh poll state, read from the primary database, so a read that
 * starts after a write's ack always includes it (KTD5).
 *
 * Polls are created with their post (`poll` on `POST /v1/content`, see
 * `refinePollPostFields`) and never edited afterwards.
 */

import { z } from 'zod'
import { PollOptionTextSchema, PollStateSchema } from '../../schemas/entities/poll'

// Re-export the poll entity so this subpath is self-sufficient
export * from '../../schemas/entities/poll'

/** Path params for every `/v1/polls/:pollId` route. */
export const PollIdParamsSchema = z.object({
  pollId: z.string().min(1)
})

export type PollIdParams = z.infer<typeof PollIdParamsSchema>

/** Path params for `DELETE /v1/polls/:pollId/options/:optionId`. */
export const PollOptionIdParamsSchema = PollIdParamsSchema.extend({
  optionId: z.string().min(1)
})

export type PollOptionIdParams = z.infer<typeof PollOptionIdParamsSchema>

/**
 * Shared response for the state read and every write: the viewer's live poll
 * state after the operation.
 */
export const PollStateResponseSchema = z.object({
  message: z.string(),
  data: PollStateSchema
})

export type PollStateResponse = z.infer<typeof PollStateResponseSchema>

/** GET /v1/polls/:pollId — live state, polled while the poll is on screen (R16). */
export const GetPollStateResponseSchema = PollStateResponseSchema

export type GetPollStateResponse = z.infer<typeof GetPollStateResponseSchema>

/**
 * PUT /v1/polls/:pollId/votes — "set my selection" (KTD4).
 *
 * Carries the voter's full set of option ids and replaces their votes, so a
 * retry is idempotent and a single-choice change is one write. An empty array
 * withdraws every vote (R7). The server refuses more than one id on a
 * single-choice poll, more than `maxVotesPerVoter` ids, and any vote on a
 * closed poll (R9).
 */
export const SetPollVotesRequestSchema = z.object({
  optionIds: z
    .array(z.string().min(1))
    .refine(ids => new Set(ids).size === ids.length, { message: 'optionIds must be unique' })
})

export type SetPollVotesRequest = z.infer<typeof SetPollVotesRequestSchema>

export const SetPollVotesResponseSchema = PollStateResponseSchema

export type SetPollVotesResponse = z.infer<typeof SetPollVotesResponseSchema>

/**
 * POST /v1/polls/:pollId/options — a voter adds an option (R8). Only when the
 * poll allows voter options and is open; the text must not match an existing
 * live option case-insensitively.
 */
export const AddPollOptionRequestSchema = z.object({
  text: PollOptionTextSchema
})

export type AddPollOptionRequest = z.infer<typeof AddPollOptionRequestSchema>

export const AddPollOptionResponseSchema = PollStateResponseSchema

export type AddPollOptionResponse = z.infer<typeof AddPollOptionResponseSchema>

/**
 * DELETE /v1/polls/:pollId/options/:optionId — the poster or a school admin
 * removes a voter-added option and the votes on it (R21).
 */
export const RemovePollOptionResponseSchema = PollStateResponseSchema

export type RemovePollOptionResponse = z.infer<typeof RemovePollOptionResponseSchema>

/**
 * POST /v1/polls/:pollId/close — the poster or a school admin closes the poll
 * (R12). Idempotent: closing a closed poll returns its state.
 */
export const ClosePollResponseSchema = PollStateResponseSchema

export type ClosePollResponse = z.infer<typeof ClosePollResponseSchema>

/** A voter as shown in the voter sheet. */
export const PollVoterSchema = z.object({
  /** User public id. */
  id: z.string(),
  name: z.string(),
  photoUrl: z.string().nullable()
})

export type PollVoter = z.infer<typeof PollVoterSchema>

/**
 * GET /v1/polls/:pollId/voters — who voted for each live option (R11). Only
 * the poster and school admins, and never on an anonymous poll; everyone else
 * is refused, so poll state never carries identities.
 */
export const GetPollVotersResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    pollId: z.string(),
    options: z.array(
      z.object({
        optionId: z.string(),
        voters: z.array(PollVoterSchema)
      })
    )
  })
})

export type GetPollVotersResponse = z.infer<typeof GetPollVotersResponseSchema>
